import React, { useState, useEffect } from 'react';
import { Calendar, School, Users, MapPin, Check } from 'lucide-react';
import { apiClient } from '@/utils/api';

interface CalendarOption {
  id: string;
  name: string;
  type: 'district' | 'school' | 'family' | 'personal';
  color?: string;
  description?: string;
  isActive?: boolean;
}

interface CalendarSelectorProps {
  selectedCalendars: string[];
  onCalendarToggle: (calendarId: string) => void;
  onCalendarsChange?: (calendars: string[]) => void;
  multiSelect?: boolean;
  showTypes?: ('district' | 'school' | 'family' | 'personal')[];
  className?: string;
}

export const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  selectedCalendars,
  onCalendarToggle,
  onCalendarsChange,
  multiSelect = true,
  showTypes = ['district', 'school', 'family'],
  className = ''
}) => {
  const [calendars, setCalendars] = useState<CalendarOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadCalendars();
  }, []);

  const loadCalendars = async () => {
    try {
      setLoading(true);
      
      const calendarOptions: CalendarOption[] = [];
      
      // Load district calendars if needed
      if (showTypes.includes('district')) {
        try {
          const response = await apiClient.get('/api/parent/school/districts');
          const districts = response.districts || response || [];
          (Array.isArray(districts) ? districts : []).forEach((district: any) => {
            calendarOptions.push({
              id: `district-${district.id}`,
              name: district.district_name,
              type: 'district',
              color: district.color || '#4B5563',
              description: `${district.district_code} School Year ${district.school_year}`,
              isActive: district.is_active
            });
          });
        } catch (error) {
          console.error('Failed to load district calendars:', error);
        }
      }
      
      // Load school calendars if needed
      if (showTypes.includes('school')) {
        try {
          const schoolsResponse = await apiClient.get('/api/schools');
          const schools = Array.isArray(schoolsResponse) ? schoolsResponse : (Array.isArray(schoolsResponse.data) ? schoolsResponse.data : (schoolsResponse.schools || []));
          schools.forEach((school: any) => {
            calendarOptions.push({
              id: `school-${school.id}`,
              name: school.name,
              type: 'school',
              color: school.color || '#10B981',
              description: school.address,
              isActive: true
            });
          });
        } catch (error) {
          console.error('Failed to load school calendars:', error);
        }
      }
      
      // Add family calendar options
      if (showTypes.includes('family')) {
        calendarOptions.push({
          id: 'family-schedule',
          name: 'Family Schedule',
          type: 'family',
          color: '#3B82F6',
          description: 'Custody and family events',
          isActive: true
        });
        
        calendarOptions.push({
          id: 'family-holidays',
          name: 'Holidays',
          type: 'family',
          color: '#EF4444',
          description: 'Holiday custody schedule',
          isActive: true
        });
      }
      
      setCalendars(calendarOptions);
    } catch (error) {
      console.error('Failed to load calendars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (calendarId: string) => {
    if (!multiSelect) {
      // Single select mode - replace selection
      onCalendarsChange?.([calendarId]);
    } else {
      // Multi-select mode - toggle
      onCalendarToggle(calendarId);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'district':
        return <MapPin className="w-4 h-4" />;
      case 'school':
        return <School className="w-4 h-4" />;
      case 'family':
        return <Users className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const groupedCalendars = calendars.reduce((acc, calendar) => {
    if (!acc[calendar.type]) {
      acc[calendar.type] = [];
    }
    acc[calendar.type].push(calendar);
    return acc;
  }, {} as Record<string, CalendarOption[]>);

  const typeLabels = {
    district: 'District Calendars',
    school: 'School Calendars',
    family: 'Family Calendars',
    personal: 'Personal Calendars'
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
          <div className="space-y-2">
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm ${className}`}>
      <div className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-sm font-semibold text-gray-700 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar Filters
            {selectedCalendars.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {selectedCalendars.length} selected
              </span>
            )}
          </h3>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expanded && (
          <div className="mt-4 space-y-4">
            {Object.entries(groupedCalendars).map(([type, typeCalendars]) => (
              <div key={type}>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {typeLabels[type as keyof typeof typeLabels]}
                </h4>
                <div className="space-y-1">
                  {typeCalendars.map(calendar => {
                    const isSelected = selectedCalendars.includes(calendar.id);
                    return (
                      <button
                        key={calendar.id}
                        onClick={() => handleToggle(calendar.id)}
                        className={`w-full flex items-start p-2 rounded-lg transition-all ${
                          isSelected 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        disabled={!calendar.isActive}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg mr-3"
                          style={{ backgroundColor: calendar.color ? `${calendar.color}20` : '#F3F4F6' }}
                        >
                          {getIcon(calendar.type)}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center">
                            <span className={`text-sm font-medium ${
                              isSelected ? 'text-blue-900' : 'text-gray-900'
                            } ${!calendar.isActive ? 'opacity-50' : ''}`}>
                              {calendar.name}
                            </span>
                            {!calendar.isActive && (
                              <span className="ml-2 text-xs text-gray-400">(Inactive)</span>
                            )}
                          </div>
                          {calendar.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{calendar.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0 mt-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {calendars.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No calendars available
              </p>
            )}
            
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    const allIds = calendars.map(c => c.id);
                    onCalendarsChange?.(allIds);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Select All
                </button>
                <button
                  onClick={() => onCalendarsChange?.([])}
                  className="text-xs text-gray-600 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};